'use client';

import {
	SocialLinksExperienceContentSchema,
	type TSocialLinksExperienceContent,
} from '@shared/schemas';
import { SocialLinksManager } from './SocialLinksManager';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ThemeSelector } from './ThemeSelector';
import { Sparkles, Share2, Palette } from 'lucide-react';

interface Props {
	value: Partial<TSocialLinksExperienceContent>;
	onChange: (value: Partial<TSocialLinksExperienceContent>) => void;
	onValidityChange: (valid: boolean) => void;
}

export function SocialLinksForm({ value, onChange, onValidityChange }: Props) {
	const links = value.links ?? [];
	const update = (next: Partial<TSocialLinksExperienceContent>) => {
		const updated = { ...value, ...next, type: 'social-links' as const };
		onChange(updated);
		onValidityChange(SocialLinksExperienceContentSchema.safeParse(updated).success);
	};

	return (
		<div className="space-y-6" aria-labelledby="social-links-title">
			<div>
				<h1 id="social-links-title" className="text-2xl font-bold tracking-tight text-slate-950">
					Create your social links page
				</h1>
				<p className="mt-1.5 text-sm text-slate-600">
					Give people one clean, responsive landing page for all your social channels and important
					links.
				</p>
			</div>

			{/* Section 1: Page Details */}
			<div className="rounded-2xl border border-slate-200 bg-slate-50/50 p-5 space-y-4">
				<div className="flex items-center gap-2 pb-3 border-b border-slate-200">
					<div className="p-1.5 rounded-lg bg-blue-100 text-blue-700">
						<Sparkles className="size-4" />
					</div>
					<div>
						<h2 className="text-sm font-bold text-slate-900">Page Information</h2>
						<p className="text-xs text-slate-500">The title and bio header shown on your page</p>
					</div>
				</div>

				<div className="space-y-4">
					<div className="space-y-1.5">
						<Label htmlFor="social-title" className="text-xs font-bold text-slate-700">
							Page Title <span className="text-red-500">*</span>
						</Label>
						<Input
							id="social-title"
							placeholder="e.g. Acme Studio or Jane Doe"
							value={value.title ?? ''}
							maxLength={120}
							onChange={(event) => update({ title: event.target.value })}
							className="h-10 bg-white"
						/>
					</div>
					<div className="space-y-1.5">
						<Label htmlFor="social-subtitle" className="text-xs font-bold text-slate-700">
							Bio / Tagline
						</Label>
						<Input
							id="social-subtitle"
							placeholder="e.g. Designer & Digital Creator based in New York"
							value={value.subtitle ?? ''}
							maxLength={240}
							onChange={(event) => update({ subtitle: event.target.value })}
							className="h-10 bg-white"
						/>
					</div>
				</div>
			</div>

			{/* Section 2: Page Theme & Header Styling */}
			<div className="rounded-2xl border border-slate-200 bg-white p-5 space-y-4 shadow-xs">
				<div className="flex items-center gap-2 pb-3 border-b border-slate-100">
					<div className="p-1.5 rounded-lg bg-indigo-100 text-indigo-700">
						<Palette className="size-4" />
					</div>
					<div>
						<h2 className="text-sm font-bold text-slate-900">Aesthetic Theme &amp; Brand Colors</h2>
						<p className="text-xs text-slate-500">
							Pick a modern theme and color scheme for your social bio page
						</p>
					</div>
				</div>

				<ThemeSelector
					selectedTheme={value.theme || 'glass'}
					onThemeChange={(theme) => update({ theme })}
					accentColor={value.accentColor || '#1d4ed8'}
					onAccentColorChange={(accentColor) => update({ accentColor, headerColor: accentColor })}
					headerColor={value.headerColor || value.accentColor || '#1d4ed8'}
					onHeaderColorChange={(headerColor) => update({ headerColor })}
				/>
			</div>

			{/* Section 3: Social Links Manager */}
			<div className="rounded-2xl border border-slate-200 bg-slate-50/50 p-5 space-y-4">
				<div className="flex items-center gap-2 pb-3 border-b border-slate-200">
					<div className="p-1.5 rounded-lg bg-emerald-100 text-emerald-700">
						<Share2 className="size-4" />
					</div>
					<div>
						<h2 className="text-sm font-bold text-slate-900">Your Channels & Links</h2>
						<p className="text-xs text-slate-500">
							Add, organize, and reorder all your social links
						</p>
					</div>
				</div>

				<SocialLinksManager links={links} onChange={(nextLinks) => update({ links: nextLinks })} />
			</div>
		</div>
	);
}
